import React, { useState, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import useTags from "hooks/tags/useTags";
import { PM_SERVICE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

export const TagManager = ({
  entityType,
  entityId,
  projectId,
  currentTags = [],
  onTagsChange,
  readOnly = false,
  compact = false,
  className = "",
}) => {
  const { tags: allTags, loading } = useTags();

  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(new Set());
  const [selectedTagId, setSelectedTagId] = useState("");

  const apiUrl = (() => {
    switch (entityType) {
      case "task":
        return `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks/${entityId}/tags`;
      case "iteration":
        return `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/iterations/${entityId}/tags`;
      case "project":
        return `${PM_SERVICE_BASE_URL}/api/v1/projects/${entityId}/tags`;
      default:
        throw new Error(`Unsupported entity type: ${entityType}`);
    }
  })();

  const assignedIds = new Set(
    currentTags.map((t) => (typeof t === "string" ? t : t.id)),
  );

  const availableTags = allTags.filter((t) => !assignedIds.has(t.id));

  const addTag = useCallback(async () => {
    if (!selectedTagId || adding) return;

    try {
      setAdding(true);

      await apiClient.post(apiUrl, [selectedTagId]);

      const added = allTags.find((t) => t.id === selectedTagId);

      toast.success("Tag added", { theme: "colored" });

      onTagsChange?.([...currentTags, added]);
      setSelectedTagId("");
    } catch (err) {
      console.error("Add tag failed:", err.response?.data || err);
      toast.error("Failed to add tag", { theme: "colored" });
    } finally {
      setAdding(false);
    }
  }, [selectedTagId, adding, apiUrl, allTags, currentTags, onTagsChange]);

  const removeTag = useCallback(async (tagId) => {
    if (!tagId) return;

    try {
      setRemoving((prev) => new Set(prev).add(tagId));

      await apiClient.delete(apiUrl, {
        params: { tagIds: [tagId] },
      });

      toast.success("Tag removed", { theme: "colored" });

      onTagsChange?.(currentTags.filter((t) => (t.id || t) !== tagId));
    } catch (err) {
      console.error("Remove tag failed:", err.response?.data?.message || err.message);
      toast.error("Failed to remove tag", { theme: "colored" });
    } finally {
      setRemoving((prev) => {
        const next = new Set(prev);
        next.delete(tagId);
        return next;
      });
    }
  }, [apiUrl, currentTags, onTagsChange]);

  if (loading && !allTags.length) {
    return (
      <div className="d-flex align-items-center gap-2 py-2">
        <Spinner animation="border" size="sm" />
        <small className="text-muted">Loading tags…</small>
      </div>
    );
  }

  return (
    <div className={`tag-manager ${className}`}>
      {/* Tags Display Section */}
      <div className="tags-display">
        {currentTags.length > 0 ? (
          <div className="d-flex flex-column gap-2">


            {/* Tags Grid - Clean and Modern */}
            <div className="d-flex flex-wrap gap-2">
              {currentTags.map((tag) => {
                const tagObj =
                  typeof tag === "string" ? allTags.find((t) => t.id === tag) : tag;

                if (!tagObj) return null;

                const isRemoving = removing.has(tagObj.id);

                return (
                  <div
                    key={tagObj.id}
                    className="badge d-flex align-items-center gap-1 px-3 py-2 bg-white text-dark border border-light shadow-sm"
                    style={{
                      borderRadius: "20px",
                      fontSize: "0.85rem",
                      fontWeight: 500,
                      transition: "all 0.2s ease",
                      cursor: "default",
                      border: "1px solid #e9ecef",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                    }}
                  >
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: "16px",
                        height: "16px",
                        background: "linear-gradient(135deg, #667eea 0%, #4b9ca2ff 100%)"
                      }}
                    >
                      <FontAwesomeIcon
                        icon="tag"
                        style={{ fontSize: "0.6rem", color: "white" }}
                      />
                    </div>
                    <span className="text-truncate" style={{ maxWidth: "120px" }}>
                      {tagObj.name}
                    </span>

                    {!readOnly && (
                      <button
                        aria-label={`Remove tag ${tagObj.name}`}
                        className="btn btn-sm p-0 border-0 bg-transparent text-muted ms-1"
                        onClick={() => removeTag(tagObj.id)}
                        disabled={isRemoving}
                        style={{
                          fontSize: "0.75rem",
                          lineHeight: 1,
                          transition: "all 0.2s ease",
                          borderRadius: "50%",
                          width: "18px",
                          height: "18px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#f8f9fa";
                          e.target.style.color = "#dc3545";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = "transparent";
                          e.target.style.color = "#6c757d";
                        }}
                      >
                        {isRemoving ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <FontAwesomeIcon icon="times" />
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="text-center py-3">
            <div className="d-flex flex-column align-items-center gap-1">
              <FontAwesomeIcon
                icon="tags"
                className="text-muted"
                style={{ fontSize: "1.2rem", opacity: 0.6 }}
              />
              <small className="text-muted">
                {readOnly ? "No tags" : "No tags yet"}
              </small>
            </div>
          </div>
        )}
      </div>

      {/* Add Tag Section - Compact and Elegant */}
      {!readOnly && availableTags.length > 0 && (
        <div className="add-tag-section py-3">
          <div className="d-flex gap-1 align-items-center" style={{ borderRadius: "6px", padding: "0.25rem" }}>
            <div className="flex-grow-1" style={{ maxWidth: 160 }}>
              <select
                className="form-select form-select-sm border-0 bg-white"
                value={selectedTagId}
                onChange={(e) => setSelectedTagId(e.target.value)}
                disabled={adding}
                style={{
                  fontSize: "0.75rem",
                  borderRadius: "4px",
                  boxShadow: "none",
                  height: "35px",
                }}
              >
                <option value="" disabled hidden>
                  Add tag...
                </option>
                {availableTags.map((tag) => (
                  <option key={tag.id} value={tag.id}>
                    {tag.name}
                  </option>
                ))}
              </select>
            </div>

            <button
              className={`btn btn-success d-flex align-items-center justify-content-center ${!selectedTagId || adding ? "opacity-50" : ""
                }`}
              disabled={!selectedTagId || adding}
              onClick={addTag}
              style={{
                borderRadius: "4px",
                fontWeight: 500,
                width: "28px",
                height: "28px",
                padding: "0",
                fontSize: "0.75rem",
                transition: "all 0.2s ease"
              }}
            >
              {adding ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <FontAwesomeIcon icon="plus" size="xs" />
              )}
            </button>
          </div>
        </div>
      )}

      {/* Success State - Minimal */}
      {!readOnly && !availableTags.length && currentTags.length > 0 && (
        <div className="mt-3 text-center">
          <small className="text-success fw-medium">
            <FontAwesomeIcon icon="check-circle" className="me-1" />
            All tags assigned
          </small>
        </div>
      )}
    </div>
  );
};
